import { nanoid } from 'nanoid';
import { IndexedDBFactory, IndexedDBManager } from '../../utils/IndexedDBManager';
import { FileUploader, IUploadCompleteInfo } from '../../notes/generic-notes/attachments/FileUploader';
import { arrayBufferToFile, getExtensionFromMimeType } from '../../utils/Audio';
import dayjs from 'dayjs';
import { BufferedEventBus } from '../../utils/BufferedEventBus';
import { IRecording, IRecordingData } from './RecordingState';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { AlertPresenter } from '../../AlertBinder';
import { Logger } from '../../support/Logger';
import { NetworkAdapter } from '../../support/NetworkAdapter';
import { OfflineRecordingQueue } from './OfflineRecordingQueue';
import { CareMapNoteTracker } from '../../notes/model/CareMapNoteTracker';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { isAutomatedTests } from '../../utils/isAutomatedTests';
import { CareMapState } from '../../caremap/CareMapState';

export const RECORDING_SERVICE_STATUS_EVENT = 'RecordingService.status-updated';
export const RECORDING_ERROR_EVENT = 'RecordingService.error';
export class RecordingService {
    static inject = () => [
        IndexedDBFactory,
        FileUploader,
        BufferedEventBus,
        CaremapNotesModelFactory,
        AlertPresenter,
        Logger,
        NetworkAdapter,
        OfflineRecordingQueue,
        CareMapNoteTracker,
        MixPanelEventEmitter,
        CareMapState
    ];

    private recordingDB: IndexedDBManager<IRecording>;

    constructor(
        private indexedDBFactory: IndexedDBFactory<IRecording>,
        private uploader: FileUploader,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private alert: AlertPresenter,
        private logger: Logger,
        private networkAdapter: NetworkAdapter,
        private offlineQueue: OfflineRecordingQueue,
        private noteTracker: CareMapNoteTracker,
        private mixpanel: MixPanelEventEmitter,
        private careMapState: CareMapState
    ) {
        // get or create the IndexedDB for innerhive
        this.recordingDB = this.indexedDBFactory.get('innerhive', 'recordings');
    };

    /**
     * Strips data from recording that should not be stored in note's otherAttributes
     * - file: ArrayBuffer is too large for note storage
     * - careMapId: note becomes source of truth for which care map
     */
    stripRecordingDataForNote(recording: IRecording): Omit<IRecording, 'file' | 'careMapId'> {
        const stripped: any = { ...recording };
        delete stripped.file;
        delete stripped.careMapId;
        return stripped;
    }

    add = async (recording: IRecordingData, extras: {skipAIProcessing?: boolean; appointmentId?: string, autoSaved?: boolean, wasInterrupted?: boolean}) => {
        const id = nanoid();
        const isOnline = this.networkAdapter.isOnline;

        // Capture the active care map ID
        const careMapId = this.careMapState.getActiveCaremap()?.careMapId || '';

        // Don't set noteId until server-side note actually exists
        let noteVM: any = null;

        if (isOnline) {
            noteVM = this.notesFactory.createFormForNewNote(
                { section: 'patient' },
                { title: 'Audio Note' }
            );
        }

        const recordToAdd: IRecording = {
            ...recording,
            id,
            url: '',
            noteId: '', // Don't set noteId until server-side note exists
            careMapId, // Store which care map this recording belongs to
            status: isOnline ? 'recording-complete' : 'offline-pending',
            skipAIProcessing: extras.skipAIProcessing,
            appointmentId: extras.appointmentId,
            byteLength: recording.file.byteLength,
            created: dayjs().utc().toISOString(),
            fileName: `${(new Date()).toLocaleDateString()} Recording.${getExtensionFromMimeType(recording.mimeType)}`
        };

        // Always save to IndexedDB first
        await this.recordingDB.add(recordToAdd);

        if (isOnline && noteVM) {
            // Online: Save note first, then update recording with real noteId
            await this.saveNoteAndUpload(noteVM, recordToAdd, extras);
        } else {
            // Offline: Queue for later processing
            this.offlineQueue.enqueue({
                recordingId: recordToAdd.id,
                action: async () => {
                    return this.createNoteAndUpload(recordToAdd, extras);
                }
            });

            // Emit success modal event for offline recordings too
            this.events.emit('RECORDING_SAVED_SUCCESS', {
                recordingId: recordToAdd.id,
                isAutoSaved: extras.autoSaved,
                isOffline: true,
                wasInterrupted: extras.wasInterrupted
            });
        }

        return recordToAdd;
    };

    private createNoteAndUpload = async (recording: IRecording, extras: {skipAIProcessing?: boolean; appointmentId?: string, autoSaved?: boolean, wasInterrupted?: boolean}) => {
        // Create fresh note
        const noteVM = this.notesFactory.createFormForNewNote(
            { section: 'patient' },
            { title: 'Audio Note' }
        );

        // Set recording in the note's otherAttributes before saving
        const note = noteVM.getNote();
        // Strip file and careMapId before storing in note
        note.otherAttributes.recording = this.stripRecordingDataForNote(recording);
        noteVM.setNote(note);

        try {
            await noteVM.save({
                trackingFrom: 'Offline Recording',
                skipToast: true
            });

            // Emit success modal event instead of showing toast
            this.events.emit('RECORDING_SAVED_SUCCESS', {
                recordingId: recording.id,
                isAutoSaved: extras.autoSaved,
                wasInterrupted: extras.wasInterrupted
            });

            // Only set noteId after successful server-side save
            const updatedRecording: IRecording = {
                ...recording,
                noteId: noteVM.getNote().noteId,
                status: 'recording-complete'
            };

            // Update in IndexedDB with real noteId
            await this.recordingDB.update(updatedRecording);

            // Track successful offline recording sync
            this.mixpanel.track('Offline Recording Synced', () => {
                const totalSeconds = Math.floor(updatedRecording.msDuration / 1000);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return {
                    'Duration': `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`,
                    'Has Appointment': Boolean(updatedRecording.appointmentId),
                    'Skip AI Processing': Boolean(updatedRecording.skipAIProcessing)
                };
            });

            // Start upload after note is saved and recording is updated
            this.upload(updatedRecording);
        } catch (error) {
            // If note save fails, revert to offline-pending to retry later
            const failedRecording: IRecording = {...recording, status: 'offline-pending'};
            await this.recordingDB.update(failedRecording);
            throw error;
        }
    };

    private saveNoteAndUpload = async (noteVM: any, recording: IRecording, extras: {skipAIProcessing?: boolean; appointmentId?: string, autoSaved?: boolean, wasInterrupted?: boolean}) => {
        // Set recording in the note's otherAttributes before saving
        const note = noteVM.getNote();
        // Strip file and careMapId before storing in note
        note.otherAttributes.recording = this.stripRecordingDataForNote(recording);
        noteVM.setNote(note);

        try {
            await noteVM.save({
                trackingFrom: 'Recording',
                skipToast: true
            });

            // Emit success modal event instead of showing toast
            this.events.emit('RECORDING_SAVED_SUCCESS', {
                recordingId: recording.id,
                isAutoSaved: extras.autoSaved,
                wasInterrupted: extras.wasInterrupted
            });

            // Only set noteId after successful server-side save
            const updatedRecording: IRecording = {
                ...recording,
                noteId: noteVM.getNote().noteId
                // Keep status as 'recording-complete' since it was already set for online recordings
            };

            // Update in IndexedDB with real noteId
            await this.recordingDB.update(updatedRecording);

            // Start upload after note is saved and recording is updated
            this.upload(updatedRecording);
        } catch (error) {
            // If note save fails, revert to offline-pending to retry later
            const failedRecording: IRecording = {...recording, status: 'offline-pending'};
            await this.recordingDB.update(failedRecording);
            throw error;
        }
    };

    get = async (id: IDBValidKey) => {
        return await this.recordingDB.get(id);
    };

    getAll = async () => {
        return await this.recordingDB.getAll();
    };

    delete = (id: IDBValidKey) => {
        this.recordingDB.delete(id);
    };

    upload = async (record: IRecording) => {
        // Check network status before attempting upload
        if (!this.networkAdapter.isOnline) {
            // Queue for later if offline
            this.offlineQueue.enqueue({
                recordingId: record.id,
                action: () => this.upload(record)
            });
            return;
        }

        const newRecord: IRecording = {...record, ...{status: 'uploading'}};

        await this.recordingDB.update(newRecord);
        this.events.emit(RECORDING_SERVICE_STATUS_EVENT, newRecord);

        // try uploading 3 times, then stop trying and change status to 'upload-failed'
        const maxAttempts = 3;
        const uploadFailed = async () => {
            const failedRecord: IRecording = {...record, ...{status: 'upload-failed'}};
            await this.recordingDB.update(failedRecord);
            this.events.emit(RECORDING_SERVICE_STATUS_EVENT, failedRecord);
        };
        const attemptUpload = async (attempt = 0) => {
            if (attempt < maxAttempts) {
                const mimeType = newRecord.mimeType;
                try {
                    // Check network status before each attempt
                    if (!this.networkAdapter.isOnline) {
                        throw new Error('Device went offline during upload');
                    }

                    await this.uploader.upload(
                        [arrayBufferToFile(
                            newRecord.file,
                            newRecord.fileName,
                            mimeType
                        )],
                        (fileInfo: IUploadCompleteInfo) => {
                            const uploadedRecord: IRecording = {...newRecord, ...{status: 'uploaded', url: fileInfo.update.url || ''}};

                            this.recordingDB.update(uploadedRecord);

                            // there is a fileInfo.size if we want, or can get it out of uploadedRecord.file ArrayBuffer
                            this.events.emit(RECORDING_SERVICE_STATUS_EVENT, uploadedRecord);
                        },
                        (_failedFile: File) => {
                            // log to Sentry if we tried 3 times to no avail
                            if (attempt === maxAttempts) {
                                // TODO: probably should be a custom alert, and a button to navigate to the
                                //      Record tab on home, so they see and can click the Retry button
                                //this.alert.showAndLogError(new Error('failed to upload new recording after 3 attempts'));
                                this.logger.error('failed to upload new recording after 3 attempts');
                                uploadFailed();
                            } else {
                                attemptUpload(++attempt);
                            }
                        }
                    );
                } catch (error) {
                    // If network error, queue for retry when online
                    const errorMessage = (error as Error).message || '';
                    if (errorMessage.includes('offline') || errorMessage.includes('network')) {
                        this.offlineQueue.enqueue({
                            recordingId: record.id,
                            action: () => this.upload(record)
                        });
                        return;
                    }

                    // update record status to upload-failed
                    uploadFailed();

                    // log to Sentry for other errors
                    this.logger.error(error);

                    // emit error event to show modal
                    this.events.emit(RECORDING_ERROR_EVENT, {
                        recordingId: record.id,
                        errorType: 'upload-failed',
                        title: 'Unable to Upload',
                        message: 'Your recording is safely saved on your device and will automatically upload once your connection improves.'
                    });
                }
            }
        };

        attemptUpload();
    };

    // New method to retry failed uploads
    retryUpload = async (recordingId: string) => {
        const recording = await this.get(recordingId);

        if (!recording) {
            this.logger.error('Recording not found for retry', { recordingId });
            return;
        }

        if (recording && (recording.status === 'upload-failed' || recording.status === 'offline-pending' || recording.status === 'uploading')) {
            if (!recording.noteId || recording.noteId === '') {
                // Need to create a note first since noteId isn't set
                await this.createNoteAndUpload(recording, {
                    skipAIProcessing: recording.skipAIProcessing,
                    appointmentId: recording.appointmentId
                });
            } else {
                // Just retry upload since note already exists
                await this.upload(recording);
            }
        } else if (recording) {
            // Don't log this error when automated tests are running
            if (!isAutomatedTests()) {
                this.logger.error('Recording not eligible for retry', { recording });
            }
        }
    };

    // Get offline recordings
    getOfflineRecordings = async (): Promise<IRecording[]> => {
        const allRecordings = await this.getAll();
        return allRecordings.filter(r => r.status === 'offline-pending');
    };

    // Dev tool: Add test recording with specific status
    addTestRecording = async (params: {
        status: IRecording['status'];
        mimeType?: string;
        msDuration?: number;
        transcription?: string;
        skipAIProcessing?: boolean;
        shouldShowErrorModal?: boolean;
    }) => {
        const id = nanoid();
        const careMapId = this.careMapState.getActiveCaremap()?.careMapId || '';

        // Create a real note first so the recording appears in the UI
        const noteVM = this.notesFactory.createFormForNewNote(
            { section: 'patient' },
            { title: 'Test Audio Note' }
        );

        // Create minimal fake audio data
        const fakeAudioData = new ArrayBuffer(100);

        const testRecording: IRecording = {
            id,
            fileName: `Test Recording ${Date.now()}.${params.mimeType === 'audio/m4a' ? 'm4a' : 'mp4'}`,
            file: fakeAudioData,
            url: params.status === 'uploaded' ? 'https://example.com/test.mp4' : '',
            noteId: '',
            status: params.status,
            careMapId,
            mimeType: params.mimeType || 'audio/mp4',
            msDuration: params.msDuration || 120000,
            byteLength: fakeAudioData.byteLength,
            created: dayjs().utc().toISOString(),
            transcription: params.transcription,
            skipAIProcessing: params.skipAIProcessing ?? true
        };

        // Set recording in note's otherAttributes before saving
        const note = noteVM.getNote();
        note.otherAttributes.recording = this.stripRecordingDataForNote(testRecording);
        noteVM.setNote(note);

        // Save the note
        await noteVM.save({
            trackingFrom: 'Test Recording',
            skipToast: true
        });

        // Update recording with real noteId
        testRecording.noteId = noteVM.getNote().noteId;

        // Save to IndexedDB
        await this.recordingDB.add(testRecording);

        // Emit status event to update UI
        this.events.emit(RECORDING_SERVICE_STATUS_EVENT, testRecording);

        // If requested and status is an error status, emit error event after delay
        // to allow the test recording modal to close first
        if (params.shouldShowErrorModal && this.isErrorStatus(params.status)) {
            setTimeout(() => {
                this.emitErrorModalForStatus(params.status, testRecording.id);
            }, 350); // Ionic modal animation duration
        }

        return testRecording;
    };

    private isErrorStatus(status: IRecording['status']): boolean {
        return status === 'upload-failed' ||
               status === 'transcribe-failed' ||
               status === 'summarize-failed';
    }

    private emitErrorModalForStatus(status: IRecording['status'], recordingId: string) {
        const errorMessages: Record<string, { errorType: string; title: string; message: string }> = {
            'upload-failed': {
                errorType: 'upload-failed',
                title: 'Unable to Upload',
                message: 'Your recording is safely saved on your device and will automatically upload once your connection improves.'
            },
            'transcribe-failed': {
                errorType: 'processing-failed',
                title: 'Processing Issue',
                message: 'Innerhive ran into a problem while processing your recording. Please try hard closing and relaunching the app to try again, or contact us for help troubleshooting.'
            },
            'summarize-failed': {
                errorType: 'processing-failed',
                title: 'Processing Issue',
                message: 'Innerhive ran into a problem while processing your recording. Please try hard closing and relaunching the app to try again, or contact us for help troubleshooting.'
            }
        };

        const errorData = errorMessages[status];
        if (errorData) {
            this.events.emit(RECORDING_ERROR_EVENT, {
                recordingId,
                ...errorData
            });
        }
    }
};
